import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react"; import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment';

import { useDebounce } from '../../../hooks/useDebounce';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { Grid } from '@mui/material';
import { GroupedContainer } from '../../ContratosCreation/components/GroupedContainer';
import { RenderInputByTypeMobile } from '../../../components/formGroupGenerator/RenderInputByTypeMobile';
import GET_TRAMO_DETALLE, { adaptTramo } from '../../../data/graphQL/queries/Tramos/getTramoDetalle';
import UPDATE_TRAMO from '../../../data/graphQL/mutations/Tramos/updateTramo';
import CREATE_TRAMO, { adaptAddTramoInput } from '../../../data/graphQL/mutations/Tramos/createTramo';

const idUsuarioLoggeado = sessionStorage.getItem('idUsuario');

export const TramoCreationContent = ({ setChildrenAction }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    idTramo,
    isNew,
    allowedWrite,
    delegaciones,
    tipoProfesionales
  } = location.state;

  const [tramoFormData, setTramoFormData] = useState({
    valor: 0,
    activo: false
  });

  const [getTramo, { data: dataTramo }] = useLazyQuery(GET_TRAMO_DETALLE, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataTramo) return;
    if (!dataTramo.tramoById) return toast.error('No se pudo cargar la información del tramo, intentelo nuevamente más tarde.');
    setTramoFormData(adaptTramo(dataTramo?.tramoById));
  }, [dataTramo]);

  const [createTramo, { loading: createLoading }] = useMutation(CREATE_TRAMO, {
    onCompleted: () => {
      toast.success(`Tramo creado con éxito.`);
      navigate(`/honorarios/adminTramos`);
    }
  });

  const [updateTramo, { loading: updateLoading }] = useMutation(UPDATE_TRAMO, {
    onCompleted: () => {
      toast.success(`Honorario actualizado con éxito.`);
      navigate(`/honorarios/adminTramos`);
    },
    onError: () => {
      toast.error('Error al actualizar el honorario, intentelo nuevamente más tarde.');
    }
  });

  const saveLoading = useMemo(() => createLoading || updateLoading, [createLoading, updateLoading]);
  const saveDisabled = useMemo(() => !allowedWrite, [allowedWrite]);

  const renderInputGroups = useMemo(() => {
    return structuredClone(inputGroups).
      map((inputGroup) => {
        return {
          ...inputGroup,
          fields: inputGroup.fields.map((input) => {
            const updatedInput = { ...input };
            switch (input.name) {
              case 'usuario':
                break;
              case 'idDelegacion':
                updatedInput.disabled = !isNew;
                updatedInput.options = delegaciones?.filter((x) => x.displayName !== 'TODAS');
                break;
              case 'idTipoProfesional':
                updatedInput.disabled = !isNew;
                updatedInput.options = tipoProfesionales;
                break;
              default:
                break;
            }
            if (!allowedWrite) {
              updatedInput.disabled = true;
            }
            return updatedInput;
          })
        };
      }
      );
  }, [allowedWrite, delegaciones, idUsuarioLoggeado, tipoProfesionales]);

  const handleChange = useCallback((name, value) => {
    setTramoFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const validateStage = () => {
    const errorMessages = [];

    if (!tramoFormData.idDelegacion) {
      errorMessages.push('Debe elegir una delegación.\n');
    }
    if (!tramoFormData.descripcion?.trim()) {
      errorMessages.push('Debe ingresar una descripción.\n');
    }
    if (!tramoFormData.idTipoProfesional) {
      errorMessages.push('Debe seleccionar un tipo de profesional.\n');
    }
    if (!tramoFormData.valor) {
      errorMessages.push('Debe ingresar un valor.\n');
    }
    if (!tramoFormData.fechaExpira) {
      errorMessages.push('Debe ingresar una fecha expiración.\n');
    }

    if (errorMessages.length > 0) {
      toast.error(errorMessages.join(' '));
      return false;
    }

    return true;
  };

  const handleSave = () => {
    if (!validateStage()) return;

    try {
      const adaptedData = adaptAddTramoInput(tramoFormData);
      if (isNew) {
        //Creación
        createTramo({
          variables: {
            input: adaptedData
          }
        });
      } else {
        updateTramo({
          variables: {
            input: adaptedData,
            idTramo: tramoFormData.id
          }
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    if (!setChildrenAction) return;
    setChildrenAction(() =>
      <div className='header-actions-calidad'>
        {idTramo > 0 && <div className='chip-estado-calidad'>
          <p>Id de Tramo: <strong>{idTramo}</strong></p>
        </div>}
        <div className='top-buttons'>
          {allowedWrite &&
            <CustomButton
              label='Guardar'
              onClick={handleSave}
              isLoading={saveLoading}
              disabled={saveDisabled}
              variant='contained' />

          }
        </div>
      </div>
    );
  }, [setChildrenAction, allowedWrite, idTramo, tramoFormData, saveLoading]);

  useEffect(() => {
    if (!idTramo || isNew) return;
    getTramo({
      variables: {
        idTramo: idTramo
      }
    });
  }, [idTramo]);

  return (
    <div>
      <Grid container spacing={2}>
        {renderInputGroups.
          map((group, index) =>
            <GroupedContainer
              ungrouped={!group.name}
              sectionTitle={group.name}
              key={index}>

              <Grid container spacing={2} size={{ xs: 12 }}>
                {group.fields.map((field, fieldIndex) => {
                  return (
                    <Grid key={`group_${index}-field_${fieldIndex}`} size={{ xs: 12, sm: 6, md: 4, lg: field.col }}>
                      <RenderInputByTypeMobile
                        input={field}
                        handleChange={handleChange}
                        value={tramoFormData[field.name]} />

                    </Grid>);

                })}
              </Grid>
            </GroupedContainer>
          )
        }
      </Grid>
    </div>);

};

const inputGroups = [
  {
    fields: [
      {
        displayName: "Delegación",
        name: "idDelegacion",
        required: true,
        type: "select",
        col: 4
      },
      {
        displayName: "Descripción",
        name: "descripcion",
        required: true,
        type: "string",
        col: 4
      },
      {
        displayName: "Tipo profesional",
        name: "idTipoProfesional",
        required: true,
        type: "select",
        col: 4
      },
      {
        displayName: "Valor",
        name: "valor",
        required: true,
        min: 0,
        type: "currency",
        col: 2
      },
      {
        displayName: "Fecha expira",
        name: "fechaExpira",
        required: true,
        type: "date",
        col: 2
      },
      {
        displayName: "Activo",
        name: "activo",
        required: true,
        type: "custom_switch",
        col: 2
      }]

  }];